"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, Check, Minus } from "lucide-react";

/* ── Provider icon ───────────────────────────────────────────────────── */
interface ProviderIconProps {
  src: string;
  bg: string;
  /** extra classes on the outer wrapper, e.g. size override */
  className?: string;
  /** img size class, defaults to h-5 w-5 */
  imgClass?: string;
  border?: boolean;
}

function ProviderIcon({ src, bg, className = "h-7 w-7", imgClass = "h-5 w-5", border }: ProviderIconProps) {
  return (
    <div
      className={`shrink-0 rounded-[6px] flex items-center justify-center overflow-hidden ${border ? "ring-1 ring-black/10" : ""} ${className}`}
      style={{ background: bg }}
      aria-hidden
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt="" className={`${imgClass} object-contain`} loading="lazy" decoding="async" />
    </div>
  );
}

/* ── Provider data ───────────────────────────────────────────────────── */
const BASE_PROVIDERS = [
  {
    name: "askFinz",
    storage: 0.020,
    egress: 0.000,
    eu: true,
    twoTier: true,
    accent: true,
    note: "No egress fees · 2-tier EU redundancy · SOC 2, ISO 27001, HIPAA, GDPR",
    iconSrc: "/icon.png",
    iconBg: "#059669",
    iconImgClass: "h-6 w-6",
    iconBorder: false,
  },
  {
    name: "AWS S3",
    storage: 0.023,
    egress: 0.090,
    eu: false,
    twoTier: false,
    note: "$0.09/GB egress. First 100 GB/mo free.",
    iconSrc: "/api/favicon?d=aws.amazon.com",
    iconBg: "#232F3E",
    iconBorder: false,
  },
  {
    name: "GCP Cloud Storage",
    storage: 0.020,
    egress: 0.120,
    eu: false,
    twoTier: false,
    note: "$0.12/GB egress from first byte — no free tier.",
    iconSrc: "/api/favicon?d=cloud.google.com",
    iconBg: "#FFFFFF",
    iconBorder: true,
  },
  {
    name: "Azure Blob Storage",
    storage: 0.018,
    egress: 0.087,
    eu: false,
    twoTier: false,
    note: "$0.087/GB egress. First 5 GB/mo free.",
    iconSrc: "/api/favicon?d=azure.microsoft.com",
    iconBg: "#FFFFFF",
    iconBorder: true,
  },
  {
    name: "DigitalOcean Spaces",
    storage: 0.020,
    egress: 0.010,
    eu: false,
    twoTier: false,
    note: "$0.010/GB egress. First 1 TB/mo free.",
    iconSrc: "/api/favicon?d=digitalocean.com",
    iconBg: "#FFFFFF",
    iconBorder: true,
  },
  {
    name: "Vultr Object Storage",
    storage: 0.020,
    egress: 0.010,
    eu: false,
    twoTier: false,
    note: "$0.010/GB egress. First 1 TB/mo free.",
    iconSrc: "/api/favicon?d=vultr.com",
    iconBg: "#FFFFFF",
    iconBorder: true,
  },
  {
    name: "Linode Object Storage",
    storage: 0.020,
    egress: 0.005,
    eu: false,
    twoTier: false,
    note: "$0.005/GB egress. First 1 TB/mo free.",
    iconSrc: "/api/favicon?d=linode.com",
    iconBg: "#FFFFFF",
    iconBorder: true,
  },
];

/* ── Helpers ─────────────────────────────────────────────────────────── */
function Tick({ on, accent }: { on: boolean; accent?: boolean }) {
  return on ? (
    <Check className={`mx-auto size-4 ${accent ? "text-[hsl(var(--accent))]" : "text-emerald-500"}`} />
  ) : (
    <Minus className="mx-auto size-4 text-foreground/20" />
  );
}

function fmtStorage(n: number) {
  return `$${n.toFixed(3)}`;
}

function fmtGb(n: number) {
  if (n >= 1024) return `${(n / 1024).toFixed(n % 1024 === 0 ? 0 : 1)} TB`;
  return `${n} GB`;
}

/* ── Component ───────────────────────────────────────────────────────── */
export function StorageComparisonSection({ storageRate = 0.020 }: { storageRate?: number }) {
  const [storageGb, setStorageGb] = useState(1000);
  const [egressGb, setEgressGb] = useState(500);

  const PROVIDERS = [
    { ...BASE_PROVIDERS[0], storage: storageRate },
    ...BASE_PROVIDERS.slice(1),
  ];

  function monthlyCost(p: (typeof PROVIDERS)[number]) {
    return storageGb * p.storage + egressGb * p.egress;
  }

  const askfinzCost = monthlyCost(PROVIDERS[0]);
  const maxCost = Math.max(...PROVIDERS.map(monthlyCost));

  return (
    <div className="mt-10 space-y-10">
      {/* ── Comparison table ── */}
      <div className="overflow-x-auto rounded-2xl border hairline-strong bg-card">
        <table className="w-full min-w-[680px] border-collapse text-left">
          <thead>
            <tr className="border-b border-border bg-card/95 backdrop-blur">
              <th className="px-5 py-4 text-xs font-medium uppercase tracking-[0.15em] text-foreground/50">
                Provider
              </th>
              <th className="px-5 py-4 text-right text-xs font-medium uppercase tracking-[0.15em] text-foreground/50">
                Storage / GB / mo
              </th>
              <th className="px-5 py-4 text-right text-xs font-medium uppercase tracking-[0.15em] text-foreground/50">
                Egress / GB
              </th>
              <th className="px-5 py-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-foreground/50">
                EU residency
              </th>
              <th className="px-5 py-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-foreground/50">
                2-tier redundancy
              </th>
            </tr>
          </thead>
          <tbody>
            {PROVIDERS.map((p, i) => (
              <tr
                key={p.name}
                className={`border-b border-border/70 ${
                  p.accent
                    ? "bg-[hsl(var(--accent))]/[0.04]"
                    : i % 2 === 0
                    ? ""
                    : "bg-foreground/[0.015]"
                }`}
              >
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <ProviderIcon
                      src={p.iconSrc}
                      bg={p.iconBg}
                      imgClass={("iconImgClass" in p ? p.iconImgClass : undefined) ?? "h-5 w-5"}
                      border={"iconBorder" in p ? p.iconBorder : false}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-sm font-medium ${
                            p.accent ? "text-foreground" : "text-foreground/80"
                          }`}
                        >
                          {p.name}
                        </span>
                        {p.accent && (
                          <span className="rounded-full bg-[hsl(var(--accent))]/15 px-2 py-0.5 text-[10px] font-mono text-[hsl(var(--accent))]">
                            You&apos;re here
                          </span>
                        )}
                      </div>
                      {p.note && (
                        <p className="mt-0.5 text-[11px] text-foreground/40">{p.note}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <span
                    className={`font-mono text-sm tabular-nums ${
                      p.accent
                        ? "font-semibold text-[hsl(var(--accent))]"
                        : "text-foreground/70"
                    }`}
                  >
                    {fmtStorage(p.storage)}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <span
                    className={`font-mono text-sm tabular-nums ${
                      p.egress === 0
                        ? "font-semibold text-emerald-600 dark:text-emerald-400"
                        : "text-foreground/70"
                    }`}
                  >
                    {p.egress === 0 ? "Free" : `$${p.egress.toFixed(3)}`}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-center">
                  <Tick on={p.eu} accent={p.accent} />
                </td>
                <td className="px-5 py-3.5 text-center">
                  <Tick on={p.twoTier} accent={p.accent} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t hairline px-5 py-3 text-[11px] text-foreground/40">
          Rates as of 2026. Egress compared at standard internet-delivery tier.
          Azure Hot tier. AWS us-east-1. GCP us-central1. EU-region pricing may
          differ slightly.{" "}
          <Link
            href="/infrastructure"
            className="underline underline-offset-2 hover:text-foreground/60"
          >
            See askFinz infrastructure →
          </Link>
        </div>
      </div>

      {/* ── Savings calculator ── */}
      <div className="rounded-2xl border hairline-strong bg-card p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">Calculate your savings</h3>
            <p className="mt-1 text-sm text-foreground/60">
              Slide to your usage — see what each provider charges per month.
            </p>
          </div>
          <div className="shrink-0 rounded-xl border hairline bg-emerald-600/5 px-4 py-2.5 text-right">
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-foreground/50">
              Your askFinz bill
            </p>
            <p className="mt-0.5 font-mono text-2xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
              ${askfinzCost.toFixed(2)}
              <span className="text-sm font-normal text-foreground/50">/mo</span>
            </p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-mono uppercase tracking-[0.15em] text-foreground/55">
                Storage used
              </label>
              <span className="font-mono text-sm font-medium">
                {fmtGb(storageGb)}
              </span>
            </div>
            <input
              type="range"
              min={10}
              max={51200}
              step={10}
              value={storageGb}
              onChange={(e) => setStorageGb(Number(e.target.value))}
              className="mt-2 w-full accent-emerald-600"
            />
            <div className="mt-1 flex justify-between text-[10px] text-foreground/35">
              <span>10 GB</span>
              <span>50 TB</span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-mono uppercase tracking-[0.15em] text-foreground/55">
                Monthly downloads
              </label>
              <span className="font-mono text-sm font-medium">
                {fmtGb(egressGb)}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={51200}
              step={10}
              value={egressGb}
              onChange={(e) => setEgressGb(Number(e.target.value))}
              className="mt-2 w-full accent-emerald-600"
            />
            <div className="mt-1 flex justify-between text-[10px] text-foreground/35">
              <span>0 GB</span>
              <span>50 TB</span>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {PROVIDERS.map((p) => {
            const cost = monthlyCost(p);
            const saving = cost - askfinzCost;
            const barWidth = maxCost > 0 ? Math.max(2, (cost / maxCost) * 100) : 2;
            return (
              <div key={p.name}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <ProviderIcon
                      src={p.iconSrc}
                      bg={p.iconBg}
                      className="h-5 w-5 shrink-0"
                      imgClass="h-3.5 w-3.5"
                      border={"iconBorder" in p ? p.iconBorder : false}
                    />
                    <span
                      className={`text-sm truncate ${
                        p.accent ? "font-semibold text-foreground" : "text-foreground/75"
                      }`}
                    >
                      {p.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {!p.accent && saving > 0.005 && (
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                        Save ${saving.toFixed(2)}/mo
                      </span>
                    )}
                    <span
                      className={`font-mono text-sm font-medium tabular-nums ${
                        p.accent
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-foreground/70"
                      }`}
                    >
                      ${cost.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-foreground/10">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      p.accent ? "bg-emerald-600" : "bg-foreground/20"
                    }`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-foreground/40">
            Savings vs. the provider&apos;s lowest comparable region. Excludes
            operational and request costs.
          </p>
          <Link
            href="/beta"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border hairline-strong px-4 py-2 text-xs font-medium text-foreground/80 transition-transform hover:-translate-y-0.5 hover:text-foreground"
          >
            Get access <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </div>

      {/* ── Why it matters callout ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          {
            stat: "Up to 98%",
            label: "cheaper egress than AWS",
            sub: "We pay $0 to deliver your files. We charge $0.",
          },
          {
            stat: "2-tier",
            label: "EU-resident redundancy",
            sub: "Primary + live mirror. Automatic switchover in under 60 seconds.",
          },
          {
            stat: `$${storageRate.toFixed(3)}/GB`,
            label: "flat storage rate",
            sub: "13% below AWS. Equal to GCP. Volume discounts from 200 GB.",
          },
        ].map((c) => (
          <div
            key={c.stat}
            className="rounded-2xl border hairline bg-card p-5"
          >
            <p className="display-serif text-2xl font-semibold tracking-tight">
              {c.stat}
            </p>
            <p className="mt-1 text-sm font-medium">{c.label}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-foreground/55">
              {c.sub}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
